import {useState, useMemo, useEffect} from "react";
import {Gavel, Zap} from "lucide-react";
import logo from "../assets/madfreight-logo.png";

const formatCurrency = (value: string) => {
    const num = parseFloat(value.replace(/[^0-9.]/g, ""));
    if (isNaN(num)) return "";
    return `$${num.toLocaleString("en-US", {minimumFractionDigits: 0, maximumFractionDigits: 0})}`;
};
export type BidItem = {
    id: number
    label: string
    amount: number
    type: "bid" | "book"
}
export type BidRates = {
    moneyMaker: string
    breakEven: string
    maxBid: string
    lastShip: string
    recBid: string
    suggestedBid: string

}

interface NegotiatorWidgetProps {
    isHotLoad?: boolean;
    onHotLoadChange?: (value: boolean) => void;
    bids?: BidItem[]
    bidRates: BidRates
}

const NegotiatorWidget = ({isHotLoad = false, onHotLoadChange, bidRates, bids}: NegotiatorWidgetProps) => {
    const [moneyMaker, setMoneyMaker] = useState(bidRates.moneyMaker || "");
    const [breakEven, setBreakEven] = useState(bidRates.breakEven || "");
    const [maxBid, setMaxBid] = useState(bidRates.maxBid) || "";
    const [lastShip, setLastShip] = useState(bidRates.lastShip || "");
    const [recBid, setRecBid] = useState(bidRates.lastShip || "");
    const [suggestedBid, setSuggestedBid] = useState(bidRates.suggestedBid || "");
    useEffect(() => {
        setMoneyMaker(bidRates.moneyMaker || "")
        setBreakEven(bidRates.breakEven || "")
        setMaxBid(bidRates.maxBid || "")
        setSuggestedBid(bidRates.suggestedBid || "")
        setLastShip(bidRates.lastShip || "")
        setRecBid(bidRates.lastShip || "")

    }, [bidRates]);
    /* {id: 1, label: "Book Now", amount: 1400, type: "book" as const},
             {id: 2, label: "Bid", amount: 1300, type: "bid" as const},*/
    const incomingBids: any[] = useMemo(() => bids || [], []);

    const parseVal = (v: string) => {
        const num = parseFloat(v.replace(/[^0-9.]/g, ""));
        return isNaN(num) ? 0 : num;
    };

    const recBidNum = useMemo(() => {
        const num = parseVal(recBid);
        return num === 0 ? null : num;
    }, [recBid]);

    const isCustomerPickup = useMemo(() => {
        return parseVal(moneyMaker) === 0 || parseVal(breakEven) === 0;
    }, [moneyMaker, breakEven]);

    const hasInvalidInputs = useMemo(() => {
        return parseVal(moneyMaker) === 0 || parseVal(breakEven) === 0 || parseVal(maxBid) === 0 || parseVal(suggestedBid) === 0;
    }, [moneyMaker, breakEven, maxBid, suggestedBid]);

    const arrowPosition = useMemo(() => {
        if (!recBidNum) return 50;
        const mm = parseFloat(moneyMaker?.replace(/[^0-9.]/g, "")) || 0;
        const mb = parseFloat(maxBid?.replace(/[^0-9.]/g, "")) || 1;
        if (mb <= mm) return 50;
        const pct = ((recBidNum - mm) / (mb - mm)) * 100;
        return Math.max(5, Math.min(95, pct));
    }, [recBidNum, moneyMaker, maxBid]);

    const handleInput = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/[^0-9]/g, "");
        setter(raw);
    };

    return (
        <div
            className={`flex flex-col mx-4 items-center w-full max-w-3xl  mt-10 gap-1 rounded-2xl transition-all duration-500 ${isHotLoad ? "animate-hot-pulse p-4 bg-hot-load-bg border-2 border-hot-load" : ""}`}>
            {/* Hot Load Banner */}
            {isHotLoad && (
                <div className="w-full rounded-xl overflow-hidden mb-2">

                    <div
                        className="flex items-center justify-center gap-3 py-2 px-4 bg-gradient-to-r from-hot-load via-hot-load-glow to-hot-load animate-hot-shimmer"
                        style={{backgroundSize: "200% 100%"}}
                    >
                        <span className="text-2xl">🔥</span>
                        <span
                            className="text-sm md:text-base font-black tracking-[0.25em] text-primary-foreground uppercase">
              Hot Load — Book Now
            </span>
                        <span className="text-2xl">🔥</span>
                    </div>
                </div>
            )}

            {/* Logo */}
            <img src={logo} alt="MadFreight Negotiator"
                 className={`w-[600px] md:w-[780px] mb-2 select-none pointer-events-none ${isHotLoad ? "-mt-4" : "-mt-14"}`}
                 style={{imageRendering: "auto"}}
            />


            {/* Gradient Bar with positioned Recommended Bid */}
            <div className="relative w-full">
                {/* Recommended Bid Badge - positioned on bar */}
                {!hasInvalidInputs && (
                    <div
                        className="absolute z-10 flex flex-col items-center transition-all duration-500"
                        style={{left: `${arrowPosition}%`, transform: "translateX(-50%)"}}
                    >
                        {/* Outer shield shape */}
                        <div className="relative">
                            {/* Gold outer border */}
                            <div
                                className="absolute -inset-[3px] bg-gradient-to-b from-[hsl(var(--shield-border-from))] via-secondary to-[hsl(var(--shield-border-to))] rounded-t-xl"
                                style={{clipPath: "polygon(0 0, 100% 0, 100% 70%, 50% 100%, 0 70%)"}}
                            />
                            {/* Dark green inner */}
                            <div
                                className="relative bg-gradient-to-b from-[hsl(var(--shield-from))] to-[hsl(var(--shield-to))] px-6 pt-2 pb-6 rounded-t-lg"
                                style={{clipPath: "polygon(0 0, 100% 0, 100% 70%, 50% 100%, 0 70%)"}}
                            >
                <span
                    className="text-[10px] font-black tracking-[0.2em] text-secondary uppercase drop-shadow-md block text-center">
                  Recommended Bid
                </span>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="$0"
                                    value={suggestedBid ? formatCurrency(suggestedBid) : ""}
                                    onChange={handleInput(setSuggestedBid)}
                                    className="text-2xl md:text-3xl font-black text-secondary drop-shadow-lg bg-transparent outline-none text-center w-36 placeholder:text-secondary/40 -mt-1 block mx-auto"
                                />
                            </div>
                        </div>
                        {/* Arrow pointer below shield */}
                        <div
                            className="w-0 h-0 border-l-[14px] border-r-[14px] border-t-[16px] border-l-transparent border-r-transparent border-t-secondary -mt-[2px] drop-shadow-lg"/>
                    </div>
                )}

                {/* Spacer for badge height */}
                <div className="h-32"/>

                {/* Bar wrapper */}
                <div className="relative w-full">
                    {/* Incoming Bid markers - below bar */}
                    {/* Last 3 Ship marker - above bar */}
                    {lastShip && parseVal(lastShip) > 0 && (() => {
                        const ls = parseFloat(lastShip) || 0;
                        const mm = parseFloat(moneyMaker) || 0;
                        const mb = parseFloat(maxBid) || 1;
                        const pct = mb <= mm ? 50 : Math.max(2, Math.min(98, ((ls - mm) / (mb - mm)) * 100));
                        return (
                            <div
                                className="absolute bottom-full mb-1 flex flex-col items-center transition-all duration-500 z-20"
                                style={{left: `${pct}%`, transform: "translateX(-50%)"}}
                            >
                                <div
                                    className="bg-foreground text-card text-[10px] font-black px-2.5 py-1 rounded-md shadow-lg tracking-wider">
                                    LAST 3
                                </div>
                                <div
                                    className="w-0 h-0 border-l-[7px] border-r-[7px] border-t-[8px] border-l-transparent border-r-transparent border-t-foreground"/>
                            </div>
                        );
                    })()}
                    {/* Bar */}
                    <div
                        className="relative w-full h-10 rounded-lg overflow-hidden border-[3px] border-foreground/80 shadow-xl">
                        <div className="absolute inset-0 bg-gradient-to-r from-bar-from via-bar-via to-bar-to"/>
                        {recBidNum && (
                            <div
                                className="absolute top-0 h-full w-1 bg-secondary shadow-lg transition-all duration-500"
                                style={{left: `${arrowPosition}%`}}
                            />
                        )}
                        {/* Last 3 vertical line on bar */}
                        {lastShip && parseVal(lastShip) > 0 && (() => {
                            const ls = parseFloat(lastShip) || 0;
                            const mm = parseFloat(moneyMaker) || 0;
                            const mb = parseFloat(maxBid) || 1;
                            const pct = mb <= mm ? 50 : Math.max(2, Math.min(98, ((ls - mm) / (mb - mm)) * 100));
                            return (
                                <div
                                    className="absolute top-0 h-full w-[3px] bg-foreground/80 transition-all duration-500 z-10"
                                    style={{left: `${pct}%`, transform: "translateX(-50%)"}}
                                />
                            );
                        })()}
                        {/* Incoming bid lines on bar */}
                        {incomingBids.map((bid) => {
                            const mm = parseFloat(moneyMaker) || 0;
                            const mb = parseFloat(maxBid) || 1;
                            const pct = mb <= mm ? 50 : Math.max(2, Math.min(98, ((bid.amount - mm) / (mb - mm)) * 100));
                            const color = bid.type === "book" ? "bg-book-now" : "bg-bid-marker";
                            return (
                                <div
                                    key={bid.id}
                                    className={`absolute top-0 h-full w-[3px] ${color} transition-all duration-500 z-10`}
                                    style={{left: `${pct}%`, transform: "translateX(-50%)"}}
                                />
                            );
                        })}
                    </div>
                    {/* Incoming bid markers below bar */}
                    {incomingBids.map((bid) => {
                        const mm = parseFloat(moneyMaker) || 0;
                        const mb = parseFloat(maxBid) || 1;
                        const pct = mb <= mm ? 50 : Math.max(2, Math.min(98, ((bid.amount - mm) / (mb - mm)) * 100));
                        const isBook = bid.type === "book";
                        const bgColor = isBook ? "bg-book-now" : "bg-bid-marker";
                        const borderColor = isBook ? "border-b-book-now" : "border-b-bid-marker";
                        return (
                            <div
                                key={bid.id}
                                className="absolute top-full mt-1 flex flex-col items-center transition-all duration-500 z-20"
                                style={{left: `${pct}%`, transform: "translateX(-50%)"}}
                            >
                                <div
                                    className={`w-0 h-0 border-l-[7px] border-r-[7px] border-b-[8px] border-l-transparent border-r-transparent ${borderColor}`}/>
                                <div
                                    className={`${bgColor} text-primary-foreground text-[10px] font-black px-2.5 py-1 rounded-md shadow-lg tracking-wider flex items-center gap-1`}>
                                    {isBook ? <Zap size={14}/> : <Gavel size={14}/>}
                                    ${bid.amount.toLocaleString()}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Customer Pickup Notice */}
            {isCustomerPickup && (
                <div className="w-full bg-destructive/10 border-2 border-destructive rounded-xl p-4 mt-2 text-center">
                    <p className="text-sm font-bold text-foreground uppercase tracking-wider mb-1">
                        Customer Pick Up Order?
                    </p>
                    <p className="text-2xl md:text-3xl font-black text-foreground tracking-wide">
                        NO BUILT IN FREIGHT
                    </p>
                </div>
            )}

            {/* Three value boxes */}
            <div className="grid grid-cols-3 gap-3 w-full mt-12">
                <div className="flex flex-col items-center bg-maker-bg rounded-xl p-3 shadow-sm">
                    <span className="text-lg font-bold text-money-maker">$$ Maker</span>
                    <input
                        type="text"
                        inputMode="numeric"
                        placeholder="$0"
                        value={moneyMaker ? formatCurrency(moneyMaker) : ""}
                        onChange={handleInput(setMoneyMaker)}
                        className="w-full text-center text-3xl md:text-4xl font-black text-money-maker bg-transparent outline-none placeholder:text-money-maker/40"
                    />
                </div>
                <div className="flex flex-col items-center bg-even-bg rounded-xl p-3 shadow-sm">
                    <span className="text-lg font-bold text-break-even">Break Even</span>
                    <input
                        type="text"
                        inputMode="numeric"
                        placeholder="$0"
                        value={breakEven ? formatCurrency(breakEven) : ""}
                        onChange={handleInput(setBreakEven)}
                        className="w-full text-center text-3xl md:text-4xl font-black text-break-even bg-transparent outline-none placeholder:text-break-even/40"
                    />
                </div>
                <div className="flex flex-col items-center bg-bid-bg rounded-xl p-3 shadow-sm">
                    <span className="text-lg font-bold text-max-bid">Max Bid</span>
                    <input
                        type="text"
                        inputMode="numeric"
                        placeholder="$0"
                        value={maxBid ? formatCurrency(maxBid) : ""}
                        onChange={handleInput(setMaxBid)}
                        className="w-full text-center text-3xl md:text-4xl font-black text-max-bid bg-transparent outline-none placeholder:text-max-bid/40"
                    />
                </div>
            </div>

            {/* Last 3 Ship */}
            <div className="mt-6 w-full">
                <div className="flex items-center w-full">
                    <div className="flex-1 h-[3px] bg-foreground/70"/>
                    <span
                        className="px-4 text-xl md:text-2xl font-black text-foreground whitespace-nowrap">Last 3 Ship:</span>
                    <input
                        type="text"
                        inputMode="numeric"
                        placeholder="$0"
                        value={lastShip ? formatCurrency(lastShip) : ""}
                        onChange={handleInput(setLastShip)}
                        className="text-xl md:text-2xl font-black text-foreground bg-transparent outline-none w-28 text-center placeholder:text-foreground/30"
                    />
                    <div className="flex-1 h-[3px] bg-foreground/70"/>
                </div>
            </div>
        </div>
    );
};

export default NegotiatorWidget;
